/**
 * SimulationContext — OpenAI Realtime Edition
 *
 * Responsibilities:
 * - Connect to OpenAI Realtime WebSocket using an ephemeral token
 * - Stream microphone audio (PCM16) to the WS
 * - Receive + play back AI audio (PCM16 via AudioContext)
 * - Accumulate a live transcript
 * - Count up an elapsed timer
 * - Autosave every 30s via POST /autosave
 * - Expose endSession() to finalize and trigger /complete
 */

import {
    createContext,
    useContext,
    useState,
    useRef,
    useCallback,
    useEffect,
    useMemo,
    type ReactNode,
} from 'react';
import { simulationService } from '../services/simulationService';
import type { TranscriptTurn, CreateSimulationData } from '../services/simulationService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConnState = 'IDLE' | 'CONNECTING' | 'CONNECTED' | 'ERROR' | 'ENDED';

interface SimRoomState {
    connectionState: ConnState;
    /** AI is currently outputting audio */
    aiSpeaking: boolean;
    /** User's VAD detected speech */
    userSpeaking: boolean;
    /** Streaming caption text from the AI */
    aiCaptionText: string;
    /** Full accumulated transcript (both sides) */
    transcript: TranscriptTurn[];
    /** Elapsed seconds since session started */
    elapsedSeconds: number;
    /** Session time limit (from creation response) */
    timeLimitSeconds: number;
    /** Mic muted by user */
    isMicMuted: boolean;
}

interface SimulationContextType extends SimRoomState {
    /** Call once with session data from /create-simulation or /resume */
    initSession: (data: CreateSimulationData & { transcript_context?: TranscriptTurn[] }) => void;
    toggleMic: () => void;
    endSession: () => Promise<string | null>; // returns simulation_id or null
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SimulationContext = createContext<SimulationContextType | undefined>(undefined);

// Singleton state to enforce 1-session-at-a-time across remounts
let globalActiveSimId: string | null = null;
let globalCleanup: (() => void) | null = null;
let globalLifecycleId = 0;

console.log('[Simulation] Bundle loaded. Global ID reset.');

export function SimulationProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<SimRoomState>({
        connectionState: 'IDLE',
        aiSpeaking: false,
        userSpeaking: false,
        aiCaptionText: '',
        transcript: [],
        elapsedSeconds: 0,
        timeLimitSeconds: 1800,
        isMicMuted: false,
    });

    // Refs — avoid stale closure issues
    const simIdRef = useRef<string | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const elapsedRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const autosaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const transcriptRef = useRef<TranscriptTurn[]>([]);
    const lastSavedIdxRef = useRef(0); // index up to which we've autosaved
    const isInitializingRef = useRef(false);
    const isSessionActiveRef = useRef(false);
    const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const instanceId = useRef(Math.random().toString(36).substring(7)).current;

    // Audio Scheduling Queue
    const nextPlaybackTimeRef = useRef(0);
    const unmuteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Speech clearing timeout
    const speechClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        console.log(`[Simulation] Provider Mounted: inst=${instanceId}`);
        return () => console.log(`[Simulation] Provider Unmounted: inst=${instanceId}`);
    }, [instanceId]);

    const setConn = useCallback((s: ConnState) =>
        setState(prev => ({ ...prev, connectionState: s })), []);

    /** Encode Float32 PCM to PCM16 base64 for OpenAI Realtime */
    function float32ToBase64PCM16(float32: Float32Array): string {
        const pcm16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
            const clamped = Math.max(-1, Math.min(1, float32[i]));
            pcm16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
        }
        const bytes = new Uint8Array(pcm16.buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
    }

    /** Decode base64 PCM16 and play via AudioContext */
    /** Decode base64 PCM16 and play via AudioContext (with scheduled playback) */
    function playPCM16Chunk(base64: string, localId: number) {
        // Defensive check: Even if the handler somehow survived, never play if ID is stale
        if (localId !== globalLifecycleId) return;
        if (!audioCtxRef.current || !isSessionActiveRef.current) return;

        const ctx = audioCtxRef.current;
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const pcm16 = new Int16Array(bytes.buffer);
        const float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 0x8000;

        const buffer = ctx.createBuffer(1, float32.length, 24000);
        buffer.getChannelData(0).set(float32);

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);

        // Scheduled Playback: ensure no overlap/jitter between tiny chunks
        const duration = buffer.duration;
        const now = ctx.currentTime;
        const startTime = Math.max(now, nextPlaybackTimeRef.current);

        source.start(startTime);
        source.playbackRate.value = 1.1; // 1.1x speed for better pacing
        nextPlaybackTimeRef.current = startTime + (duration / 1.1);
    }

    // ---------------------------------------------------------------------------
    // OpenAI Realtime WebSocket
    // ---------------------------------------------------------------------------

    function handleWsMessage(event: MessageEvent, localId: number, wsId: string) {
        if (localId !== globalLifecycleId) return;

        let msg: any;
        try { msg = JSON.parse(event.data); } catch { return; }

        // Trace Log (Enhanced with event_id and timestamp)
        if (msg.type === 'response.output_audio.delta' || msg.type === 'response.output_audio_transcript.done' || msg.type === 'error') {
            console.log(`[Simulation] 📩 ${msg.type}`, {
                inst: instanceId,
                lc: localId,
                ws: wsId,
                evt: msg.event_id || 'N/A',
                resp: msg.response_id || msg.item_id || 'N/A',
                ts: new Date().toISOString().split('T')[1].slice(0, -1) // HH:MM:SS.mmm
            });
        }

        switch (msg.type) {
            // AI audio chunk
            // GA renamed these with an 'output_' infix vs the old beta names
            // (response.audio.delta -> response.output_audio.delta, etc).
            // Verified live against the real API which event names actually fire.
            case 'response.output_audio.delta': {
                if (msg.delta) playPCM16Chunk(msg.delta, localId);
                setState(prev => {
                    if (prev.aiSpeaking) return { ...prev, aiSpeaking: true };
                    // Auto-mute on first chunk
                    mediaStreamRef.current?.getTracks().forEach(t => { t.enabled = false; });
                    return { ...prev, aiSpeaking: true, isMicMuted: true };
                });
                break;
            }
            // AI audio finished
            case 'response.output_audio.done': {
                setState(prev => ({ ...prev, aiSpeaking: false }));

                // Audio-Synced Unmute: wait for physical buffer to finish
                const now = audioCtxRef.current?.currentTime || 0;
                const remaining = Math.max(0, nextPlaybackTimeRef.current - now);
                const delayMs = remaining * 1000;

                if (unmuteTimerRef.current) clearTimeout(unmuteTimerRef.current);
                unmuteTimerRef.current = setTimeout(() => {
                    mediaStreamRef.current?.getTracks().forEach(t => { t.enabled = true; });
                    setState(prev => ({ ...prev, isMicMuted: false }));
                    unmuteTimerRef.current = null;
                }, delayMs);
                break;
            }
            // AI transcript streaming (captions)
            case 'response.output_audio_transcript.delta': {
                const delta = msg.delta || '';
                setState(prev => ({ ...prev, aiCaptionText: prev.aiCaptionText + delta }));
                break;
            }
            // Start of a new AI response - CLEAR previous caption persistent text
            case 'response.created': {
                setState(prev => ({ ...prev, aiCaptionText: '' }));
                break;
            }
            // A conversation item was created (empty placeholder)
            case 'conversation.item.created': {
                const item = msg.item;
                if (!item) break;
                // Just diagnostic logging or clearing state if needed
                // Real accumulation now happens in .completed and response.done
                break;
            }
            // User transcription finished
            case 'conversation.item.input_audio_transcription.completed': {
                const transcript = msg.transcript;
                if (transcript) {
                    const turn: TranscriptTurn = { role: 'user', content: transcript, timestamp_ms: Date.now() };
                    transcriptRef.current = [...transcriptRef.current, turn];
                    setState(prev => ({
                        ...prev,
                        transcript: transcriptRef.current,
                    }));
                }
                break;
            }
            // AI response finished (contains full transcript)
            case 'response.done': {
                const response = msg.response;
                if (response && response.output) {
                    response.output.forEach((item: any) => {
                        if (item.type === 'message' && item.role === 'assistant') {
                            // GA renamed the audio content item type to 'output_audio'
                            // (was 'audio'). Verified live against the real API.
                            const content = item.content?.find((c: any) => c.type === 'output_audio')?.transcript ||
                                item.content?.find((c: any) => c.text)?.text || '';
                            if (content) {
                                const turn: TranscriptTurn = { role: 'assistant', content, timestamp_ms: Date.now() };
                                transcriptRef.current = [...transcriptRef.current, turn];
                                setState(prev => ({
                                    ...prev,
                                    transcript: transcriptRef.current,
                                    // aiCaptionText: '', // REMOVED for persistence
                                }));
                            }
                        }
                    });
                }
                break;
            }
            // User VAD events
            case 'input_audio_buffer.speech_started': {
                setState(prev => ({ ...prev, userSpeaking: true }));
                // Clear caption after 2 seconds of user speaking
                if (speechClearTimeoutRef.current) clearTimeout(speechClearTimeoutRef.current);
                speechClearTimeoutRef.current = setTimeout(() => {
                    setState(prev => ({ ...prev, aiCaptionText: '' }));
                }, 2000);
                break;
            }
            case 'input_audio_buffer.speech_stopped':
            case 'input_audio_buffer.committed': {
                setState(prev => ({ ...prev, userSpeaking: false }));
                break;
            }
            // Session error
            case 'error': {
                console.error('[OpenAI Realtime] Error:', msg.error);
                setState(prev => ({ ...prev, connectionState: 'ERROR' }));
                break;
            }
            default:
                break;
        }
    }

    // ---------------------------------------------------------------------------
    // Microphone capture → WS
    // ---------------------------------------------------------------------------

    async function startMic(ws: WebSocket) {
        try {
            // Close existing if any
            mediaStreamRef.current?.getTracks().forEach(t => t.stop());

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
            audioCtxRef.current = audioCtx;
            const source = audioCtx.createMediaStreamSource(stream);
            // ScriptProcessor (deprecated but universally supported; fine for MVP)
            const processor = audioCtx.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;
            processor.onaudioprocess = (e) => {
                if (!isSessionActiveRef.current) return;
                if (ws.readyState !== WebSocket.OPEN) return;
                if (state.isMicMuted) return; // honour mute
                const pcm = float32ToBase64PCM16(e.inputBuffer.getChannelData(0));
                ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: pcm }));
            };
            source.connect(processor);

            // To avoid feedback (echo), we DO NOT connect to audioCtx.destination.
            // However, ScriptProcessor needs a connection to destination to fire in some browsers.
            // So we connect to a silent gain node.
            const silentGain = audioCtx.createGain();
            silentGain.gain.value = 0;
            processor.connect(silentGain);
            silentGain.connect(audioCtx.destination);
        } catch (err) {
            console.error('[Mic] Failed to access microphone:', err);
        }
    }

    const cleanupSession = useCallback(() => {
        // 1. Immediately mark as inactive to stop incoming audio/processing
        isSessionActiveRef.current = false;
        isInitializingRef.current = false;
        nextPlaybackTimeRef.current = 0;

        // 2. Clear intervals & timers
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (autosaveRef.current) {
            clearInterval(autosaveRef.current);
            autosaveRef.current = null;
        }
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }
        if (speechClearTimeoutRef.current) {
            clearTimeout(speechClearTimeoutRef.current);
            speechClearTimeoutRef.current = null;
        }
        if (unmuteTimerRef.current) {
            clearTimeout(unmuteTimerRef.current);
            unmuteTimerRef.current = null;
        }

        // 3. Close & Nullify WS (Aggressive)
        if (wsRef.current) {
            const ws = wsRef.current;
            ws.onopen = null;
            ws.onmessage = null;
            ws.onerror = null;
            ws.onclose = null;
            try {
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.close();
                }
            } catch (e) {
                console.warn('[Simulation] Error closing WS:', e);
            }
            wsRef.current = null;
        }

        // 4. Stop Mic
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            mediaStreamRef.current = null;
        }
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }

        // 5. Close AudioContext (Aggressive sound termination)
        if (audioCtxRef.current) {
            const ctx = audioCtxRef.current;
            ctx.suspend().catch(() => { });
            ctx.close().catch(() => { });
            audioCtxRef.current = null;
        }

        // 6. Final Global cleanup
        if (globalActiveSimId === simIdRef.current) {
            globalActiveSimId = null;
            globalCleanup = null;
        }
    }, []);

    // ---------------------------------------------------------------------------
    // initSession — called from the room page with data from /create or /resume
    // ---------------------------------------------------------------------------

    const initSession = useCallback((data: CreateSimulationData & { transcript_context?: TranscriptTurn[] }) => {
        // 1. Global Lifecycle Protection: Increment global counter
        // This ensures that ONLY the latest mount across the entire app is valid.
        globalLifecycleId += 1;
        const localLifecycleId = globalLifecycleId;

        console.log(`[Simulation] Initialising session. inst=${instanceId} lc=${localLifecycleId}`);

        // 2. Hard Singleton Guard: Always kill ANY existing session before starting a new one.
        if (globalCleanup) {
            console.log('[Simulation] Hard Singleton: Killing existing session before starting new one.');
            globalCleanup();
        }

        // 2. Local idempotency check (to avoid double-init in same tick)
        if ((isSessionActiveRef.current || isInitializingRef.current) && simIdRef.current === data.simulation_id) {
            return;
        }

        isInitializingRef.current = true;

        // 3. Clear existing resources first
        cleanupSession();

        // 4. Register this session as the one and only active session
        globalActiveSimId = data.simulation_id;
        globalCleanup = cleanupSession;

        simIdRef.current = data.simulation_id;
        elapsedRef.current = data.time_used_seconds ?? 0;
        transcriptRef.current = data.transcript_context ?? [];
        lastSavedIdxRef.current = transcriptRef.current.length;

        setState(prev => ({
            ...prev,
            connectionState: 'CONNECTING',
            timeLimitSeconds: data.time_limit_seconds,
            elapsedSeconds: data.time_used_seconds ?? 0,
            transcript: transcriptRef.current,
        }));

        // 5. Validation: Ensure we have a token
        if (!data.ephemeral_token) {
            console.error('[Simulation] Failed to initialize: Missing ephemeral_token.', data);
            setConn('ERROR');
            return;
        }

        // 6. Build OpenAI Realtime WS URL
        // Must match the model the ephemeral token was minted with on the
        // backend (services/simulation_service.py _get_ephemeral_token),
        // or OpenAI rejects the connection with 'invalid_model'.
        const wsUrl = `wss://api.openai.com/v1/realtime?model=gpt-realtime`;
        const wsId = Math.random().toString(36).substring(7);

        // Standard sub-protocols for browser-based ephemeral keys.
        // No 'openai-beta.realtime-v1' - the GA API rejects beta-shaped
        // connections with error code 'beta_api_shape_disabled'.
        const ws = new WebSocket(wsUrl, [
            'realtime',
            `openai-insecure-api-key.${data.ephemeral_token}`,
        ]);
        wsRef.current = ws;

        // 7. Set connection timeout (30s)
        connectionTimeoutRef.current = setTimeout(() => {
            if (localLifecycleId !== globalLifecycleId) return;
            if (!isSessionActiveRef.current && ws.readyState !== WebSocket.OPEN) {
                console.error(`[Simulation] Connection timeout inst=${instanceId} lc=${localLifecycleId}`);
                setConn('ERROR');
                cleanupSession();
            }
        }, 30_000);

        ws.onopen = () => {
            if (localLifecycleId !== globalLifecycleId) {
                console.log(`[Simulation] Zombie onopen discarded (inst=${instanceId} lc=${localLifecycleId} curr=${globalLifecycleId} ws=${wsId})`);
                ws.close();
                return;
            }
            try {
                console.log(`[Simulation] WS Connected (inst=${instanceId} lc=${localLifecycleId} ws=${wsId})`);
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }
                isInitializingRef.current = false;
                isSessionActiveRef.current = true;
                setConn('CONNECTED');

                // 1. Inject system prompt (strict persona prompt)
                // GA session shape: requires session.type, audio config moved
                // under audio.input/audio.output (was flat input_audio_format
                // etc under the old beta shape). Verified against the live
                // API - the old shape 400s with missing_required_parameter.
                ws.send(JSON.stringify({
                    type: 'session.update',
                    session: {
                        type: 'realtime',
                        output_modalities: ['audio'],
                        instructions: (data.strict_persona_prompt || '') + "\n\nCRITICAL: Be extremely concise and fast-paced. Do not waste words. Start the interview immediately.",
                        audio: {
                            input: {
                                format: { type: 'audio/pcm', rate: 24000 },
                                transcription: { model: 'whisper-1' },
                                turn_detection: {
                                    type: 'server_vad',
                                    threshold: 0.5,
                                    prefix_padding_ms: 300,
                                    silence_duration_ms: 600,
                                },
                            },
                            output: {
                                format: { type: 'audio/pcm', rate: 24000 },
                                voice: 'alloy',
                            },
                        },
                    },
                }));

                // 2. Inject prior transcript context for resume continuity
                if (data.transcript_context && data.transcript_context.length > 0) {
                    data.transcript_context.forEach(turn => {
                        ws.send(JSON.stringify({
                            type: 'conversation.item.create',
                            item: {
                                type: 'message',
                                role: turn.role,
                                content: [{ type: 'input_text', text: turn.content }],
                            },
                        }));
                    });
                }

                // 3. Prompt AI to start the interview
                ws.send(JSON.stringify({ type: 'response.create' }));

                // 4. Start microphone
                startMic(ws);

                // 5. Elapsed timer (count up)
                timerRef.current = setInterval(() => {
                    if (localLifecycleId !== globalLifecycleId) {
                        if (timerRef.current) clearInterval(timerRef.current);
                        return;
                    }
                    elapsedRef.current += 1;
                    setState(prev => ({ ...prev, elapsedSeconds: elapsedRef.current }));
                }, 1000);

                // 6. Autosave loop every 30s
                autosaveRef.current = setInterval(() => {
                    if (localLifecycleId !== globalLifecycleId) {
                        if (autosaveRef.current) clearInterval(autosaveRef.current);
                        return;
                    }
                    const delta = transcriptRef.current.slice(lastSavedIdxRef.current);
                    lastSavedIdxRef.current = transcriptRef.current.length;
                    if (simIdRef.current) {
                        simulationService.autosave(simIdRef.current, {
                            transcript_delta: delta.length > 0 ? delta : undefined,
                            time_used_seconds: elapsedRef.current,
                        }).catch(e => console.warn('[Autosave] failed:', e));
                    }
                }, 30_000);
            } catch (err) {
                console.error('[Simulation] Error during onopen initialization:', err);
                setConn('ERROR');
                cleanupSession();
            }
        };

        ws.onmessage = (e) => {
            handleWsMessage(e, localLifecycleId, wsId);
        };
        ws.onerror = (err) => {
            if (localLifecycleId !== globalLifecycleId) return;
            console.error(`[Simulation] WS Error (inst=${instanceId} lc=${localLifecycleId})`, err);
            isInitializingRef.current = false;
            setConn('ERROR');
        };
        ws.onclose = (event) => {
            if (localLifecycleId !== globalLifecycleId) return;
            console.log(`[Simulation] WS Closed (inst=${instanceId} lc=${localLifecycleId})`, event.code);
            isInitializingRef.current = false;
            isSessionActiveRef.current = false;
            setState(prev => {
                // Only transition to ENDED if we weren't already in an ERROR or ENDED state
                if (prev.connectionState !== 'ENDED' && prev.connectionState !== 'ERROR') {
                    return { ...prev, connectionState: 'ENDED' };
                }
                return prev;
            });
        };
    }, [cleanupSession, setConn, instanceId]);

    // ---------------------------------------------------------------------------
    // Mic toggle
    // ---------------------------------------------------------------------------

    const toggleMic = useCallback(() => {
        setState(prev => {
            const muted = !prev.isMicMuted;
            // Stop/resume tracks
            mediaStreamRef.current?.getTracks().forEach(t => { t.enabled = !muted; });
            return { ...prev, isMicMuted: muted };
        });
    }, []);

    // ---------------------------------------------------------------------------
    // End session
    // ---------------------------------------------------------------------------

    const endSession = useCallback(async (): Promise<string | null> => {
        const id = simIdRef.current;
        cleanupSession();

        setState(prev => ({ ...prev, connectionState: 'ENDED' }));

        if (!id) return null;

        try {
            await simulationService.complete(id, {
                final_transcript: transcriptRef.current,
                time_used_seconds: elapsedRef.current,
            });
        } catch (e) {
            console.error('[endSession] /complete failed:', e);
        }

        return id;
    }, [cleanupSession]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupSession();
        };
    }, [cleanupSession]);

    const contextValue = useMemo(() => ({
        ...state,
        initSession,
        toggleMic,
        endSession,
    }), [state, initSession, toggleMic, endSession]);

    return (
        <SimulationContext.Provider value={contextValue}>
            {children}
        </SimulationContext.Provider>
    );
}

export function useSimulation() {
    const ctx = useContext(SimulationContext);
    if (!ctx) throw new Error('useSimulation must be used within SimulationProvider');
    return ctx;
}
